import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { InsightSourceType } from "../../../insight/types/insightApi";

/**
 * Single shared "active scope" for Insight (CTX-97) — kept in Redux so
 * Topology, Inventory, CSPM Findings, Path Analysis, Optimization and future
 * AI surfaces all read/write the same selection instead of each view
 * re-deriving it from its own URL parsing. `InsightTopologyViewV2` (and any
 * sibling Insight view) should read this via `useAppSelector` rather than
 * keeping local scope state.
 *
 * The URL is the actual source of truth for deep-linking — see
 * `src/insight/hooks/useInsightScopeUrlSync.ts`, which hydrates this slice
 * from `useSearchParams` on mount and keeps both in sync on every change.
 * This slice is intentionally NOT persisted (`persistConfig.whitelist` in
 * `store/store.tsx` only includes `selection`): scope is per-navigation,
 * not a durable user preference, and a stale persisted scope would fight the
 * URL on next load.
 *
 * Changing a level always resets everything downstream of it (source ->
 * cloud -> region -> account -> resource), so an incompatible child
 * selection can never survive a parent change.
 */
export type InsightActiveMode = "topology" | "findings" | "path-analysis" | "optimization";

export interface InsightScopeState {
	sourceType: InsightSourceType | null;
	/** Canonical opaque id (BGP sidebar's `subId`, falling back to `id`) — never the incremental display id. */
	sourceId: string | null;
	sourceName: string | null;
	cloud: string | null;
	region: string | null;
	account: string | null;
	mode: InsightActiveMode;
	selectedResourceId: string | null;
}

const initialState: InsightScopeState = {
	sourceType: null,
	sourceId: null,
	sourceName: null,
	cloud: null,
	region: null,
	account: null,
	mode: "topology",
	selectedResourceId: null,
};

export interface SetSourcePayload {
	sourceType: InsightSourceType;
	sourceId: string;
	sourceName: string;
}

/** Full-state restore for deep links — no cascading resets, since the URL already carries a coherent snapshot. */
export type HydrateInsightScopePayload = Partial<InsightScopeState>;

/**
 * Activates a complete Reflector/Cluster -> Cloud -> Region -> Account path in
 * one shot, e.g. when a leaf node is clicked in the hierarchical scope
 * browser (CTX-97 UX rework). Unlike the single-level `set*` reducers, this
 * does not cascade-reset unrelated levels because the whole path is supplied
 * atomically by the caller — only `selectedResourceId` resets, since it
 * belongs to whatever topology/scope was previously active.
 */
export interface ActivateScopePayload {
	sourceType: InsightSourceType;
	sourceId: string;
	sourceName: string;
	cloud: string | null;
	region: string | null;
	account: string | null;
}

const insightScopeSlice = createSlice({
	name: "insightScope",
	initialState,
	reducers: {
		setSource: (state, action: PayloadAction<SetSourcePayload>) => {
			state.sourceType = action.payload.sourceType;
			state.sourceId = action.payload.sourceId;
			state.sourceName = action.payload.sourceName;
			state.cloud = null;
			state.region = null;
			state.account = null;
			state.selectedResourceId = null;
		},
		setCloud: (state, action: PayloadAction<string | null>) => {
			state.cloud = action.payload;
			state.region = null;
			state.account = null;
			state.selectedResourceId = null;
		},
		setRegion: (state, action: PayloadAction<string | null>) => {
			state.region = action.payload;
			state.account = null;
			state.selectedResourceId = null;
		},
		setAccount: (state, action: PayloadAction<string | null>) => {
			state.account = action.payload;
			state.selectedResourceId = null;
		},
		setMode: (state, action: PayloadAction<InsightActiveMode>) => {
			state.mode = action.payload;
		},
		setSelectedResource: (state, action: PayloadAction<string | null>) => {
			state.selectedResourceId = action.payload;
		},
		activateScope: (state, action: PayloadAction<ActivateScopePayload>) => {
			state.sourceType = action.payload.sourceType;
			state.sourceId = action.payload.sourceId;
			state.sourceName = action.payload.sourceName;
			state.cloud = action.payload.cloud;
			state.region = action.payload.region;
			state.account = action.payload.account;
			state.selectedResourceId = null;
		},
		hydrateInsightScope: (state, action: PayloadAction<HydrateInsightScopePayload>) => {
			return { ...state, ...action.payload };
		},
		resetInsightScope: () => initialState,
	},
});

export const {
	setSource,
	setCloud,
	setRegion,
	setAccount,
	setMode,
	setSelectedResource,
	activateScope,
	hydrateInsightScope,
	resetInsightScope,
} = insightScopeSlice.actions;

export default insightScopeSlice.reducer;
